import BaseGeocode from 'lib/location/Locations/Base';
import { maxMindGeolocate, html5Geolocate } from 'lib/location/Geolocate';
import { useEffect, useState } from 'react';
import { Location, setGeolocationData } from 'redux-implementations/geolocation';
import { getGeoLocation } from 'redux-implementations/selectors';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import State from 'types/State';
import Geocode from 'types/Geocode';

export const fallbackGeoLocationData = {
  city: '',
  state: undefined,
  latitude: 0,
  longitude: 0,
  zip: '',
  stateAbbreviation: '',
};

export interface SetLocationInterface {
  (location: Geocode): Promise<void>;
}

export interface UseGeoLocationResult {
  geolocation?: Geocode;
  setGeolocation: SetLocationInterface;
}

export default function useGeoLocation(shouldSkipGeolocate?: boolean): UseGeoLocationResult {
  const dispatch = useDispatch();
  const [html5Geolocation, setHtml5Geolocation] = useState<Location | null>();
  const [maxmindGeolocation, setMaxmindGeolocation] = useState<Location | null>();

  useEffect(() => {
    if (shouldSkipGeolocate !== undefined && !shouldSkipGeolocate) {
      html5Geolocate()
        .then((location) => setHtml5Geolocation(location.toJSON()))
        .catch((err) => {
          console.error(err);
          setHtml5Geolocation(null);
        });

      maxMindGeolocate()
        .then((location) => {
          setMaxmindGeolocation(location.toJSON());
          sessionStorage.setItem('geolocation', JSON.stringify(location));
        })
        .catch((err) => {
          console.error(err);
          setMaxmindGeolocation(null);
        });
    }
  }, [shouldSkipGeolocate]);

  useEffect(() => {
    const bothFailed = html5Geolocation === null && maxmindGeolocation === null;
    const shouldSet = html5Geolocation || maxmindGeolocation || bothFailed;

    if (shouldSet) {
      const bestGeolocation = html5Geolocation || maxmindGeolocation || fallbackGeoLocationData;
      dispatch(setGeolocationData(bestGeolocation));
    }
  }, [dispatch, html5Geolocation, maxmindGeolocation]);

  const setGeolocation: SetLocationInterface = async (location: Geocode): Promise<void> => {
    dispatch(setGeolocationData(location.toJSON()));
  };

  const geolocation = useGeolocationData();

  return { geolocation, setGeolocation };
}

export function useGeolocationData(): BaseGeocode | undefined {
  const geolocationData = useSelector((state: State) => getGeoLocation(state), shallowEqual);
  return geolocationData ? new BaseGeocode(geolocationData) : undefined;
}
